import { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import CodeGenerator from '../components/ai-coder/CodeGenerator';
import UsageCounter from '../components/ai-coder/UsageCounter';
import UpgradeModal from '../components/ai-coder/UpgradeModal';
import { Sparkles } from 'lucide-react';

export default function CoderAI() {
  const { user } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const usageCounterRef = useRef(null);

  const handleLimitReached = () => {
    setShowUpgradeModal(true);
  };

  const handleGenerateSuccess = () => {
    // Refresh usage counter
    if (usageCounterRef.current) {
      usageCounterRef.current.fetchUsage();
    }
  };

  const handleUpgrade = async () => {
    // Refresh user data and usage counter after upgrade
    if (usageCounterRef.current) {
      usageCounterRef.current.fetchUsage();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:bg-none dark:!bg-black">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg">
              <Sparkles size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Coder AI</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Trading Indicator Generator</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Code Generator */}
          <div className="lg:col-span-2">
            <CodeGenerator
              onLimitReached={handleLimitReached}
              onGenerateSuccess={handleGenerateSuccess}
            />
          </div>

          {/* Right Column - Usage Stats */}
          <div className="space-y-6">
            <UsageCounter
              ref={usageCounterRef}
              onUpdate={(usage) => {
                if (usage.remaining === 0 && !usage.isUnlimited) {
                  // Optional: Auto-show upgrade modal when limit reached
                  // setShowUpgradeModal(true);
                }
              }}
            />

            {/* Quick Stats */}
            <div className="bg-[#ffffff] dark:!bg-black rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Info</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Subscription Tier</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white capitalize">
                    {user?.subscription?.tier || 'Free'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Member Since</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {new Date(user?.createdAt || Date.now()).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {user?.subscription?.tier === 'free' && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Upgrade to VIP/Premium
                </button>
              )}
            </div>

            {/* Info Card */}
            <div className="bg-[#ffffff] dark:!bg-black rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/10 dark:to-blue-900/20 border-blue-200 dark:border-blue-900/30">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                How it works
              </h3>
              <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                <li className="flex gap-2">
                  <span className="text-blue-600 dark:text-blue-400">1.</span>
                  <span>Choose your programming language</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 dark:text-blue-400">2.</span>
                  <span>Describe the trading indicator you need</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 dark:text-blue-400">3.</span>
                  <span>AI generates production-ready code</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 dark:text-blue-400">4.</span>
                  <span>Copy and use in your trading platform</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
}
